import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import {Add, DeleteOutlined, EditOutlined, SearchOutlined} from '@material-ui/icons';
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import InstructionMessage from '../../../shared/instructionmessage/InstructionMessage.js';
import useConfirmationDialog from '../../../shared/confirmationdialog/useConfirmationDialog.js';
import {criarColunaOpcoes} from '../../components/ColunaOpcoes.jsx';
import DialogDocumentoInscricao from '../../dialogs/inscricao/DialogDocumentoInscricao.jsx';
import DialogDetalheDocumentoInscricao from '../../dialogs/inscricao/DialogDetalheDocumentoInscricao.jsx';
import StatusDocumentoInscricao from '../../components/inscricao/StatusDocumentoInscricao.jsx';
import {deQuem, ouTraco, situacaoDocumento} from '../../components/inscricao/documentoInscricao.js';
import {PAGINACAO_PADRAO} from '../../components/paginacao.js';
import {buttonTableCell, containedButton, tituloSecao} from '../../../../utils/styles.js';

/* Mesmos títulos das demais etapas da inscrição pública. */
const useStyles = makeStyles(theme => ({
    sectionTitle: {
        ...tituloSecao(theme),
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2)
    },
    ...buttonTableCell(theme),
    ...containedButton(theme, false, false)
}));

const mesmoDocumento = (a, b) => !!a && !!b && String(a.id) === String(b.id);

/*
 * Etapa "Documentos": anexos da inscrição, inclusive os negados. Novo anexo
 * ou troca de arquivo vão em base64 no anexo.arquivo e são gravados ao salvar
 * a inscrição. O tipo de documento é escolhido entre os do processo que ainda
 * não foram anexados. A lupa abre os detalhes do documento: arquivo,
 * avaliação vigente com o histórico e a visualização.
 */
export default function EtapaDocumentos({watch, setValue, documentosProcesso = []}) {
    const classes = useStyles();
    const confirmation = useConfirmationDialog();
    // Índice do anexo em edição: -1 para adicionar, null com o diálogo fechado.
    const [indice, setIndice] = useState(null);
    // Índice do anexo aberto na lupa (detalhes), null com o diálogo fechado.
    const [indiceDetalhe, setIndiceDetalhe] = useState(null);
    const anexos = watch('inscricaoAnexos') || [];
    const nomeInscrito = watch('pessoa.nome');
    const membros = watch('composicaoFamiliar') || [];
    const deQuemAnexo = anexo => deQuem(anexo, {nomeInscrito, composicaoFamiliar: membros});
    const anexoDetalhe = indiceDetalhe !== null ? anexos[indiceDetalhe] : null;
    const emEdicao = indice !== null && indice > -1 ? anexos[indice] : null;

    const documentosDisponiveis = documentosProcesso.filter(documento =>
        mesmoDocumento(documento, emEdicao?.documento)
        || !anexos.some(anexo => mesmoDocumento(anexo.documento, documento)));

    const salvar = anexo => {
        const atual = [...anexos];
        if (emEdicao) {
            atual[indice] = anexo;
        } else {
            atual.push(anexo);
        }
        setValue('inscricaoAnexos', atual, {shouldDirty: true});
    };

    const remover = posicao => {
        confirmation('Tem certeza que deseja remover o documento?', () =>
            setValue('inscricaoAnexos', anexos.filter((item, atual) => atual !== posicao), {shouldDirty: true}));
    };

    /*
     * Como o FormDocumentos público, a lista ocupa a largura toda (sem as seções
     * verticais das outras etapas): a tabela tem seis colunas.
     */
    const conteudo = (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <h4 className={classes.sectionTitle}>Documentos</h4>
                <InstructionMessage
                    title='Orientações sobre Documentos'
                    type='info'
                    variant='standard'
                    collapsible={true}
                    defaultExpanded={true}
                    items={[
                        'Estão listados todos os documentos anexados à inscrição, inclusive os negados na avaliação;',
                        'O tipo de documento é escolhido entre os exigidos pelo processo que ainda não foram anexados;',
                        'O arquivo deve estar em PDF com até 5 MB e é gravado ao salvar a inscrição.'
                    ]}/>
            </Grid>
            <Grid item xs={12}>
                <DataGrid
                    toolbarContent={(
                        <Button variant='contained' className={classes.containedButton} startIcon={<Add/>} onClick={() => setIndice(-1)}>
                            Adicionar
                        </Button>
                    )}
                    tableContainerProps={{component: 'div'}}
                    rows={anexos}
                    paginate
                    paginateOptions={PAGINACAO_PADRAO}
                    noRecordsText='Nenhum documento anexado.'
                    columns={[
                        {label: 'Documento', field: 'documento.nome', valueGetter: (valor, row) => ouTraco(row.documento?.nome)},
                        {label: 'De quem', field: 'composicaoFamiliar', valueGetter: (valor, row) => deQuemAnexo(row)},
                        {label: 'Arquivo', field: 'anexo.nome', valueGetter: (valor, row) => ouTraco(row.anexo?.nome)},
                        {
                            label: 'Situação', field: 'avaliacao', sortable: false,
                            valueGetter: (valor, row) => <StatusDocumentoInscricao situacao={situacaoDocumento(row)}/>
                        },
                        {label: 'Enviado em', field: 'dataEnvio', valueGetter: (valor, row) => (situacaoDocumento(row) === 'NAO_SALVO' && !row.id ? 'Não salvo' : ouTraco(row.dataEnvio))},
                        criarColunaOpcoes(row => {
                            const posicao = anexos.indexOf(row);
                            return [
                                {titulo: 'Ver detalhes', icone: <SearchOutlined/>, onClick: () => setIndiceDetalhe(posicao)},
                                {titulo: 'Editar', icone: <EditOutlined/>, onClick: () => setIndice(posicao)},
                                {titulo: 'Remover', icone: <DeleteOutlined/>, onClick: () => remover(posicao)}
                            ];
                        }, {className: classes.buttonTableCell})
                    ]}/>
            </Grid>
        </Grid>
    );

    return (
        <>
            {conteudo}
            <DialogDocumentoInscricao
                open={indice !== null}
                onClose={() => setIndice(null)}
                title={emEdicao ? 'Editar Documento' : 'Adicionar Documento'}
                value={emEdicao}
                documentos={documentosDisponiveis}
                onSubmit={salvar}/>
            <DialogDetalheDocumentoInscricao
                open={indiceDetalhe !== null}
                onClose={() => setIndiceDetalhe(null)}
                anexo={anexoDetalhe}
                deQuem={anexoDetalhe ? deQuemAnexo(anexoDetalhe) : ''}/>
        </>
    );
}

EtapaDocumentos.propTypes = {
    watch: PropTypes.func.isRequired,
    setValue: PropTypes.func.isRequired,
    documentosProcesso: PropTypes.array
};
